import { useState, useRef } from 'react';
import { useFormacao, type Formacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import RegistarParticipacaoModal from '@/pages/formacao/participantes/components/RegistarParticipacaoModal';

interface Props {
  formacao: Formacao;
  onClose: () => void;
}

const TIPOS_DOC = [
  { value: 'certificado', label: 'Certificado', icon: 'ri-medal-line',             bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { value: 'presenca',    label: 'Presença',    icon: 'ri-contacts-book-line',     bg: 'bg-zinc-100',   text: 'text-zinc-600'   },
  { value: 'programa',    label: 'Programa',    icon: 'ri-file-list-3-line',       bg: 'bg-amber-50',   text: 'text-amber-700'  },
  { value: 'financeiro',  label: 'Financeiro',  icon: 'ri-money-euro-circle-line', bg: 'bg-zinc-100',   text: 'text-zinc-600'   },
  { value: 'avaliacao',   label: 'Avaliação',   icon: 'ri-survey-line',            bg: 'bg-zinc-100',   text: 'text-zinc-600'   },
  { value: 'outro',       label: 'Outro',       icon: 'ri-file-line',              bg: 'bg-zinc-100',   text: 'text-zinc-600'   },
];

const estadoPartCfg: Record<string, { bg: string; text: string; dot: string }> = {
  'Inscrito':     { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  'A Frequentar': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  'Concluído':    { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Desistiu':     { bg: 'bg-rose-50',    text: 'text-rose-600',    dot: 'bg-rose-400'    },
  'Reprovado':    { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400'  },
};

const estadoFormCfg: Record<string, { bg: string; text: string; dot: string }> = {
  'Concluída': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Em Curso':  { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  'Planeada':  { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  'Cancelada': { bg: 'bg-rose-50',    text: 'text-rose-600',    dot: 'bg-rose-400'    },
};

const avatarPalette = [
  { bg: 'bg-rose-100', text: 'text-rose-600' },
  { bg: 'bg-violet-100', text: 'text-violet-600' },
  { bg: 'bg-amber-100', text: 'text-amber-600' },
  { bg: 'bg-teal-100', text: 'text-teal-600' },
  { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  { bg: 'bg-orange-100', text: 'text-orange-600' },
];

function getAvatar(id: string) {
  const nums = id.replace(/\D/g, '');
  const idx = nums ? parseInt(nums, 10) % avatarPalette.length : 0;
  return avatarPalette[isNaN(idx) ? 0 : idx];
}

function getInitials(nome: string) {
  return nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('').toUpperCase();
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type ActiveTab = 'participantes' | 'documentos';

export default function DetalhesFormacaoModal({ formacao, onClose }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { participantesDaFormacao, documentosDaFormacao, removerParticipante, atualizarParticipante, uploadDocumento, eliminarDocumento, getDocumentoUrl, saving, editarFormacao } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();
  const [activeTab, setActiveTab] = useState<ActiveTab>('participantes');
  const [showRegistar, setShowRegistar] = useState(false);
  const [confirmRemover, setConfirmRemover] = useState<string | null>(null);
  const [editandoEstado, setEditandoEstado] = useState<string | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Inline edit state
  const [editandoCampo, setEditandoCampo] = useState<{ id: string; campo: 'custo' | 'nota' } | null>(null);
  const [editValor, setEditValor] = useState('');

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadNome, setUploadNome] = useState('');
  const [uploadTipo, setUploadTipo] = useState('certificado');
  const [uploadDrag, setUploadDrag] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [estadoDropdownOpen, setEstadoDropdownOpen] = useState(false);

  const partsFormacao = participantesDaFormacao(formacao.id);
  const docsFormacao = documentosDaFormacao(formacao.id);
  const ec = estadoFormCfg[formacao.estado] ?? estadoFormCfg['Planeada'];

  const custoTotalParticipantes = partsFormacao.reduce((acc, p) => acc + (p.custo || 0), 0);

  const concluidos = partsFormacao.filter((p) => p.estado_participacao === 'Concluído').length;
  const certs = partsFormacao.filter((p) => p.certificado_emitido).length;

  function showSuccess(msg: string) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3500); }

  async function handleRemover(id: string) {
    await removerParticipante(id);
    setConfirmRemover(null);
  }

  async function handleEstadoChange(partId: string, novoEstado: string) {
    await atualizarParticipante(partId, { estado_participacao: novoEstado });
    setEditandoEstado(null);
  }

  async function handleInlineSave(partId: string, campo: 'custo' | 'nota') {
    const val = campo === 'custo' ? parseFloat(editValor) || 0 : parseFloat(editValor);
    if (isNaN(val)) {
      setEditandoCampo(null);
      return;
    }
    const updates = campo === 'custo' ? { custo: val } : { nota_final: val };
    await atualizarParticipante(partId, updates);
    setEditandoCampo(null);
    setEditValor('');
    showSuccess(campo === 'custo' ? 'Custo atualizado.' : 'Nota atualizada.');
  }

  function iniciarEdicao(partId: string, campo: 'custo' | 'nota', valorAtual: number | null | undefined) {
    setEditandoCampo({ id: partId, campo });
    setEditValor(valorAtual != null ? String(valorAtual) : '');
  }

  async function handleToggleCertificado(partId: string, atual: boolean) {
    await atualizarParticipante(partId, {
      certificado_emitido: !atual,
      data_certificado: !atual ? new Date().toISOString().split('T')[0] : null,
    });
  }

  function handleFileSelect(f: File) {
    setUploadFile(f);
    if (!uploadNome) setUploadNome(f.name.replace(/\.[^.]+$/, ''));
  }

  async function handleUpload() {
    if (!uploadFile || !uploadNome.trim()) return;
    const result = await uploadDocumento({
      formacao_id: formacao.id,
      nome: uploadNome.trim(),
      tipo: uploadTipo,
      file: uploadFile,
    });
    if (result) {
      setUploadFile(null);
      setUploadNome('');
      setUploadTipo('certificado');
      setShowUploadForm(false);
      showSuccess('Documento carregado com sucesso!');
    }
  }

  async function handleDeleteDoc(docId: string) {
    const doc = docsFormacao.find((d) => d.id === docId);
    if (!doc) return;
    await eliminarDocumento(doc);
    setConfirmDeleteDoc(null);
    showSuccess('Documento eliminado.');
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
        onClick={(e) => { if (e.target === e.currentTarget && !showRegistar) handleClose(); }}
      >
        <div className={`bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
          {/* Header */}
          <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEstadoDropdownOpen((v) => !v);
                      }}
                      disabled={saving}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${ec.bg} ${ec.text}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${ec.dot}`} />
                      {formacao.estado}
                      <div className="w-3 h-3 flex items-center justify-center opacity-60">
                        <i className="ri-arrow-down-s-line text-xs" />
                      </div>
                    </button>
                    {estadoDropdownOpen && (
                      <div className="absolute z-20 mt-1 left-0 bg-white border border-zinc-200 rounded-xl shadow-lg py-1 min-w-[140px]">
                        {Object.entries(estadoFormCfg).map(([key, cfg]) => (
                          <button
                            key={key}
                            onClick={async (e) => {
                              e.stopPropagation();
                              setEstadoDropdownOpen(false);
                              if (key !== formacao.estado) {
                                await editarFormacao(formacao.id, { estado: key });
                              }
                            }}
                            disabled={saving}
                            className={`w-full text-left px-3 py-1.5 text-[11px] font-semibold flex items-center gap-2 hover:bg-zinc-50 transition-colors cursor-pointer ${formacao.estado === key ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            <span className={cfg.text}>{key}</span>
                            {formacao.estado === key && <i className="ri-check-line text-xs ml-auto" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {formacao.categoria && (
                    <span className="text-[11px] font-medium bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{formacao.categoria}</span>
                  )}
                </div>
                <h2 className="text-[17px] font-bold text-zinc-900 leading-tight">{formacao.nome}</h2>
                {formacao.entidade_formadora && (
                  <p className="text-[12px] text-zinc-400 mt-1">{formacao.entidade_formadora}</p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Metadados */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {[
                { icon: 'ri-time-line',              label: 'Duração',   value: `${formacao.duracao_horas}h` },
                { icon: 'ri-calendar-line',          label: 'Datas',     value: formacao.data_inicio ? `${new Date(formacao.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}${formacao.data_fim ? ` → ${new Date(formacao.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}` : ''}` : '—' },
                { icon: 'ri-map-pin-line',           label: 'Modalidade', value: formacao.modalidade },
                { icon: 'ri-money-euro-circle-line', label: 'Custo',     value: custoTotalParticipantes > 0 ? `${Number(custoTotalParticipantes).toLocaleString('pt-PT')} €` : 'Gratuita' },
              ].map((m) => (
                <div key={m.label} className="bg-zinc-50 rounded-lg p-3 flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${m.icon} text-zinc-400 text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-zinc-400 font-medium">{m.label}</p>
                    <p className="text-[12px] font-semibold text-zinc-800 truncate">{m.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="px-7 pt-3 pb-0 border-b border-zinc-100 flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setActiveTab('participantes')}
              className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-t-lg transition-all cursor-pointer ${activeTab === 'participantes' ? 'text-zinc-900 bg-zinc-50 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-group-line text-sm" /></div>
              Participantes
              <span className="ml-0.5 text-[10px] bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded-full font-bold">{partsFormacao.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('documentos')}
              className={`whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-t-lg transition-all cursor-pointer ${activeTab === 'documentos' ? 'text-zinc-900 bg-zinc-50 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-folder-open-line text-sm" /></div>
              Documentos
              <span className="ml-0.5 text-[10px] bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded-full font-bold">{docsFormacao.length}</span>
            </button>
          </div>

          {/* Conteúdo scrollável */}
          <div className="flex-1 overflow-auto">
            {/* Toast de sucesso */}
            {successMsg && (
              <div className="mx-7 mt-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] px-3 py-2 rounded-lg">
                <i className="ri-checkbox-circle-fill text-sm" />
                {successMsg}
              </div>
            )}

            {/* ─────────── Tab Participantes ─────────── */}
            {activeTab === 'participantes' && (
              <>
                <div className="px-7 py-3.5 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
                  <div className="flex items-center gap-3">
                    <span className="text-[13px] font-bold text-zinc-800">Inscritos</span>
                    {concluidos > 0 && <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">{concluidos} concluídos</span>}
                    {certs > 0 && <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{certs} cert.</span>}
                  </div>
                  <button
                    onClick={() => setShowRegistar(true)}
                    className="whitespace-nowrap flex items-center gap-1.5 bg-zinc-900 text-white text-[12px] font-medium px-3 py-2 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-xs" /></div>
                    Adicionar
                  </button>
                </div>

                {partsFormacao.length === 0 ? (
                  <div className="py-14 flex flex-col items-center gap-3 text-zinc-300">
                    <div className="w-12 h-12 flex items-center justify-center"><i className="ri-group-line text-3xl" /></div>
                    <p className="text-[13px] font-medium text-zinc-400">Nenhum participante inscrito</p>
                    <button onClick={() => setShowRegistar(true)} className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                      Registar primeiro participante
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-50">
                    {partsFormacao.map((p) => {
                      const nome = p.funcionario_nome ?? p.funcionario_id;
                      const func = funcionarios.find((f) => f.id === p.funcionario_id);
                      const palette = getAvatar(p.funcionario_id);
                      const epc = estadoPartCfg[p.estado_participacao] ?? estadoPartCfg['Inscrito'];
                      const isEditingEstado = editandoEstado === p.id;
                      return (
                        <div key={p.id} className="px-7 py-3.5 flex items-center gap-4 hover:bg-zinc-50/50 transition-colors">
                          <div className={`w-9 h-9 flex items-center justify-center rounded-full text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
                            {getInitials(nome)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-zinc-800 truncate">{nome}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {func?.carreira && <span className="text-[10px] text-zinc-400">{func.carreira}</span>}
                              {/* Custo inline editable */}
                              {editandoCampo?.id === p.id && editandoCampo.campo === 'custo' ? (
                                <span className="flex items-center gap-1">
                                  <span className="text-zinc-200">·</span>
                                  <span className="text-[10px] text-zinc-400">€</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    autoFocus
                                    value={editValor}
                                    onChange={(e) => setEditValor(e.target.value)}
                                    onBlur={() => handleInlineSave(p.id, 'custo')}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(p.id, 'custo'); if (e.key === 'Escape') setEditandoCampo(null); }}
                                    className="w-16 text-[10px] font-medium text-emerald-600 border border-zinc-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                                  />
                                </span>
                              ) : (
                                <button
                                  onClick={() => iniciarEdicao(p.id, 'custo', p.custo)}
                                  className="flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity"
                                  title="Clique para editar custo"
                                >
                                  {p.custo !== null && p.custo > 0 ? (
                                    <>
                                      <span className="text-zinc-200">·</span>
                                      <span className="text-[10px] font-medium text-emerald-600">€{Number(p.custo).toFixed(2)}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-zinc-200">·</span>
                                      <span className="text-[10px] font-medium text-zinc-300 underline decoration-dashed">€0,00</span>
                                    </>
                                  )}
                                </button>
                              )}
                              {/* Nota inline editable */}
                              {editandoCampo?.id === p.id && editandoCampo.campo === 'nota' ? (
                                <span className="flex items-center gap-1">
                                  <span className="text-zinc-200">·</span>
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="20"
                                    autoFocus
                                    value={editValor}
                                    onChange={(e) => setEditValor(e.target.value)}
                                    onBlur={() => handleInlineSave(p.id, 'nota')}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleInlineSave(p.id, 'nota'); if (e.key === 'Escape') setEditandoCampo(null); }}
                                    className="w-10 text-[10px] font-medium text-zinc-600 border border-zinc-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                                  />
                                </span>
                              ) : (
                                <button
                                  onClick={() => iniciarEdicao(p.id, 'nota', p.nota_final)}
                                  className="flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity"
                                  title="Clique para editar nota"
                                >
                                  {p.nota_final ? (
                                    <>
                                      <span className="text-zinc-200">·</span>
                                      <span className="text-[10px] font-medium text-zinc-600">{p.nota_final}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-zinc-200">·</span>
                                      <span className="text-[10px] font-medium text-zinc-300 underline decoration-dashed">—</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="relative flex-shrink-0">
                            {isEditingEstado ? (
                              <select autoFocus value={p.estado_participacao} onChange={(e) => handleEstadoChange(p.id, e.target.value)} onBlur={() => setEditandoEstado(null)} className="text-[11px] border border-zinc-200 rounded-lg px-2 py-1 bg-white focus:outline-none cursor-pointer">
                                {Object.keys(estadoPartCfg).map((k) => <option key={k} value={k}>{k}</option>)}
                              </select>
                            ) : (
                              <button onClick={() => setEditandoEstado(p.id)} title="Clique para alterar estado" className={`whitespace-nowrap inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${epc.bg} ${epc.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${epc.dot}`} />
                                {p.estado_participacao}
                                <i className="ri-pencil-line text-[9px] opacity-60" />
                              </button>
                            )}
                          </div>
                          <button onClick={() => handleToggleCertificado(p.id, p.certificado_emitido)} disabled={saving} title={p.certificado_emitido ? 'Certificado emitido' : 'Emitir certificado'} className={`whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer flex-shrink-0 ${p.certificado_emitido ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : 'text-zinc-300 hover:text-zinc-500 hover:bg-zinc-100'}`}>
                            <i className="ri-medal-line text-sm" />
                          </button>
                          {confirmRemover === p.id ? (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={() => handleRemover(p.id)} disabled={saving} className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 px-2 py-1 rounded-lg cursor-pointer">Sim</button>
                              <button onClick={() => setConfirmRemover(null)} className="whitespace-nowrap text-[11px] text-zinc-400 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer">Não</button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmRemover(p.id)} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer flex-shrink-0">
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ─────────── Tab Documentos ─────────── */}
            {activeTab === 'documentos' && (
              <>
                <div className="px-7 py-3.5 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
                  <span className="text-[13px] font-bold text-zinc-800">{docsFormacao.length} documento(s)</span>
                  <button
                    onClick={() => { setShowUploadForm((v) => !v); }}
                    className="whitespace-nowrap flex items-center gap-1.5 bg-zinc-900 text-white text-[12px] font-medium px-3 py-2 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-line text-xs" /></div>
                    Carregar
                  </button>
                </div>

                {/* Formulário de upload inline */}
                {showUploadForm && (
                  <div className="px-7 py-4 border-b border-zinc-100 bg-zinc-50/60 space-y-3">
                    <p className="text-[12px] font-bold text-zinc-700">Novo Documento</p>
                    {/* Tipo */}
                    <div className="flex flex-wrap gap-1.5">
                      {TIPOS_DOC.map((t) => (
                        <button key={t.value} type="button" onClick={() => setUploadTipo(t.value)}
                          className={`whitespace-nowrap flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all cursor-pointer ${uploadTipo === t.value ? `${t.bg} ${t.text} border-current` : 'text-zinc-400 bg-white border-zinc-200 hover:border-zinc-300'}`}
                        >
                          <i className={`${t.icon} text-xs`} />{t.label}
                        </button>
                      ))}
                    </div>
                    {/* Nome */}
                    <input type="text" value={uploadNome} onChange={(e) => setUploadNome(e.target.value)} placeholder="Nome do documento..." className="w-full px-3 py-2 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400" />
                    {/* Drop area */}
                    <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={(e) => { if (e.target.files?.[0]) handleFileSelect(e.target.files[0]); }} />
                    <div
                      onDragOver={(e) => { e.preventDefault(); setUploadDrag(true); }}
                      onDragLeave={() => setUploadDrag(false)}
                      onDrop={(e) => { e.preventDefault(); setUploadDrag(false); if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]); }}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-4 flex items-center gap-3 cursor-pointer transition-all ${uploadDrag ? 'border-zinc-400 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/50'}`}
                    >
                      {uploadFile ? (
                        <>
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
                            <i className="ri-file-check-line text-base text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-zinc-800 truncate">{uploadFile.name}</p>
                            <p className="text-[10px] text-zinc-400">{formatBytes(uploadFile.size)} · clica para substituir</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                            <i className="ri-upload-cloud-2-line text-base text-zinc-400" />
                          </div>
                          <div>
                            <p className="text-[12px] font-semibold text-zinc-600">Arrasta ou clica para selecionar</p>
                            <p className="text-[10px] text-zinc-400">PDF, Word, Excel ou imagem · máx. 10 MB</p>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" onClick={() => { setShowUploadForm(false); setUploadFile(null); setUploadNome(''); }} className="whitespace-nowrap px-4 py-2 text-[12px] font-medium text-zinc-500 bg-zinc-100 rounded-lg hover:bg-zinc-200 cursor-pointer transition-colors">Cancelar</button>
                      <button type="button" onClick={handleUpload} disabled={saving || !uploadFile || !uploadNome.trim()} className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {saving ? <><div className="w-3 h-3 flex items-center justify-center"><i className="ri-loader-4-line text-xs animate-spin" /></div>A carregar...</> : <><div className="w-3 h-3 flex items-center justify-center"><i className="ri-upload-line text-xs" /></div>Carregar</>}
                      </button>
                    </div>
                  </div>
                )}

                {docsFormacao.length === 0 ? (
                  <div className="py-14 flex flex-col items-center gap-3 text-zinc-300">
                    <div className="w-12 h-12 flex items-center justify-center"><i className="ri-folder-open-line text-3xl" /></div>
                    <p className="text-[13px] font-medium text-zinc-400">Nenhum documento carregado</p>
                    <button onClick={() => setShowUploadForm(true)} className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-line text-sm" /></div>
                      Carregar primeiro documento
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-50">
                    {docsFormacao.map((doc) => {
                      const tc = TIPOS_DOC.find((t) => t.value === doc.tipo) ?? TIPOS_DOC[TIPOS_DOC.length - 1];
                      return (
                        <div key={doc.id} className="px-7 py-3.5 flex items-center gap-3 hover:bg-zinc-50/50 transition-colors">
                          <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${tc.bg}`}>
                            <i className={`${tc.icon} text-base ${tc.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-zinc-800 truncate" title={doc.nome}>{doc.nome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>{tc.label}</span>
                              {doc.tamanho && <span className="text-[10px] text-zinc-400">{formatBytes(doc.tamanho)}</span>}
                              <span className="text-[10px] text-zinc-400">{doc.created_at ? new Date(doc.created_at).toLocaleDateString('pt-PT') : ''}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {doc.url && (
                              <button
                                onClick={() => window.open(getDocumentoUrl(doc.url!), '_blank', 'noopener')}
                                className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                                title="Descarregar"
                              >
                                <i className="ri-download-line text-sm" />
                              </button>
                            )}
                            {confirmDeleteDoc === doc.id ? (
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleDeleteDoc(doc.id)} disabled={saving} className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 px-2 py-1 rounded-lg cursor-pointer">Sim</button>
                                <button onClick={() => setConfirmDeleteDoc(null)} className="whitespace-nowrap text-[11px] text-zinc-400 bg-zinc-100 px-2 py-1 rounded-lg cursor-pointer">Não</button>
                              </div>
                            ) : (
                              <button onClick={() => setConfirmDeleteDoc(doc.id)} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer">
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Rodapé */}
          <div className="px-7 py-4 border-t border-zinc-100 flex-shrink-0 flex items-center justify-between">
            {formacao.descricao && (
              <p className="text-[11px] text-zinc-400 flex-1 mr-4 line-clamp-1">{formacao.descricao}</p>
            )}
            <button onClick={handleClose} className="whitespace-nowrap flex-shrink-0 px-5 py-2 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer ml-auto">
              Fechar
            </button>
          </div>
        </div>
      </div>

      {showRegistar && (
        <RegistarParticipacaoModal
          formacaoPreSel={formacao}
          onClose={() => setShowRegistar(false)}
          onSuccess={() => setShowRegistar(false)}
        />
      )}
    </>
  );
}
